import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Tv, Mic, Award, Heart, Shield, ChevronRight, Target, Eye } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cheat-alerts/artifacts/gnd74fuw_Copy%20of%20CheaterBeaters%20%28Stickers%20%28Circle%29%29.jpg";

const stats = [
  { number: '10M+', label: 'Viewers', icon: Eye },
  { number: '500+', label: 'Episodes', icon: Tv },
  { number: '50+', label: 'Podcast Episodes', icon: Mic },
  { number: '1000+', label: 'Stories Shared', icon: Heart }
];

const values = [
  {
    icon: Target,
    title: 'Truth',
    description: 'We believe in uncovering the truth, no matter how difficult it may be.'
  },
  {
    icon: Shield,
    title: 'Support',
    description: 'We provide a safe space for people to share their experiences without judgment.'
  },
  {
    icon: Heart,
    title: 'Empathy',
    description: 'We approach every story with compassion and understanding.'
  },
  {
    icon: Award,
    title: 'Authenticity',
    description: 'Real stories from real people. No scripts, no actors, just raw truth.'
  }
];

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero */}
      <section className="bg-gradient-to-r from-gray-900 to-gray-800 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
                About <span className="text-red-500">Cheater Beaters</span>
              </h1>
              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                We're dedicated to bringing you the most compelling, real, and raw stories about relationships, trust, and betrayal. Our platform gives voice to those who have been wronged and provides a space for healing.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/shows"
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  <Tv size={20} />
                  Watch Shows
                </Link>
                <Link
                  to="/submit"
                  className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold transition-colors border border-white/20"
                >
                  Share Your Story
                </Link>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="relative">
                <div className="w-96 h-96 rounded-full bg-red-600/20 absolute -inset-4 blur-3xl" />
                <img
                  src={LOGO_URL}
                  alt="Cheater Beaters"
                  className="relative w-96 h-96 object-contain rounded-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <stat.icon size={24} className="text-red-600" />
                </div>
                <div className="text-3xl md:text-4xl font-black text-gray-900">{stat.number}</div>
                <div className="text-gray-500 font-medium mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-red-600 font-semibold uppercase tracking-wide">Our Mission</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mt-2 mb-6">
              REAL. RAW. UNFILTERED.
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Cheater Beaters was created to give a voice to those who have experienced betrayal in their relationships. We believe that by sharing these stories, we can help others recognize the signs, find strength in community, and ultimately heal from their experiences.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-red-600 font-semibold uppercase tracking-wide">What We Stand For</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mt-2">
              Our <span className="text-red-600">Values</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <value.icon size={28} className="text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-red-600 font-semibold uppercase tracking-wide">What We Offer</span>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900 mt-2 mb-6">
                Entertainment with <span className="text-red-600">Purpose</span>
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Tv size={24} className="text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">TV Shows</h3>
                    <p className="text-gray-600 text-sm">Watch our compelling episodes featuring real stories of betrayal and truth.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Mic size={24} className="text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Podcast</h3>
                    <p className="text-gray-600 text-sm">Listen to in-depth conversations and extended stories on our podcast.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Users size={24} className="text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Community</h3>
                    <p className="text-gray-600 text-sm">Join a supportive community of people who understand what you're going through.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Ready to Share?</h3>
                <p className="text-red-100 mb-6">
                  Your story matters. Whether you want to be featured on our show, podcast, or simply want to share your experience, we're here to listen.
                </p>
                <Link
                  to="/submit"
                  className="inline-flex items-center gap-2 bg-white text-red-600 px-6 py-3 rounded-lg font-bold hover:bg-red-50 transition-colors"
                >
                  Submit Your Story
                  <ChevronRight size={20} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
            Join the <span className="text-red-500">Community</span>
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Follow us on social media for updates, behind-the-scenes content, and more stories.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/shows"
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors"
            >
              <Tv size={20} />
              Watch Shows
            </Link>
            <Link
              to="/podcast"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors border border-white/20"
            >
              <Mic size={20} />
              Listen to Podcast
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
